import { useEffect, useMemo, useState } from 'react';
import { CiSearch } from 'react-icons/ci';
import { LuLayoutGrid, LuList } from 'react-icons/lu';
import {
  Navigation,
  UserCard,
  Pagination,
  UserListItem,
} from '../../../components';
import { useNavigate } from 'react-router-dom';
import useUsers, { UserData } from '../../../hooks/useUsers';

const USER_FILTER_TAB_STORAGE_KEY = 'adminUsersFilterTab';

const getStoredUserFilterTab = (): 'all' | 'inactive' => {
  try {
    const storedFilterTab = sessionStorage.getItem(USER_FILTER_TAB_STORAGE_KEY);

    return storedFilterTab === 'inactive' ? 'inactive' : 'all';
  } catch {
    return 'all';
  }
};

export default function UsersDashboard() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [userFilterTab, setUserFilterTab] = useState<'all' | 'inactive'>(
    getStoredUserFilterTab
  );

  const {
    getUserList,
    getInactiveUserList,
    getParticularUserByFullName,
    currentPage,
    prvPage,
    nextPage,
    goToNextPage,
    goToPreviousPage,
    allUsers,
    isFetching,
  } = useUsers();

  useEffect(() => {
    try {
      sessionStorage.setItem(USER_FILTER_TAB_STORAGE_KEY, userFilterTab);
    } catch {
      // Ignore unavailable session storage.
    }
  }, [userFilterTab]);

  const visibleUsers = useMemo<UserData[]>(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return allUsers;
    }

    return allUsers.filter(
      (user: UserData) =>
        user.first_name.toLowerCase().includes(normalizedSearchTerm) ||
        user.last_name.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [allUsers, searchTerm]);

  const handleChange = async (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      if (userFilterTab === 'inactive') {
        getInactiveUserList();
      } else {
        getUserList();
      }
      return;
    }

    if (value.length % 2 === 0) {
      await getParticularUserByFullName(value);
    }
  };

  const handleUserFilterTabChange = (filterTab: 'all' | 'inactive') => {
    setSearchTerm('');
    setUserFilterTab(filterTab);

    if (filterTab === 'inactive') {
      getInactiveUserList();
      return;
    }

    getUserList();
  };

  const goToUserForm = () => {
    navigate('/admin/users/create');
  };

  return (
    <>
      <Navigation />
      <div className="flex items-center justify-between py-4">
        <h2 className="text-xl font-medium text-slate-700 line-clamp-1 dark:text-navy-50 lg:text-2xl">
          Użytkownicy
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center rounded-lg border border-slate-200 p-1 dark:border-navy-600">
            <button
              type="button"
              aria-label="Widok kafelków"
              className={`btn h-9 px-3 ${viewMode === 'grid'
                  ? 'bg-primary text-white dark:bg-accent'
                  : 'bg-transparent text-slate-500 hover:bg-slate-100 dark:text-navy-200 dark:hover:bg-navy-600'
                }`}
              onClick={() => setViewMode('grid')}>
              <LuLayoutGrid className="text-lg" />
            </button>
            <button
              type="button"
              aria-label="Widok listy"
              className={`btn h-9 px-3 ${viewMode === 'list'
                  ? 'bg-primary text-white dark:bg-accent'
                  : 'bg-transparent text-slate-500 hover:bg-slate-100 dark:text-navy-200 dark:hover:bg-navy-600'
                }`}
              onClick={() => setViewMode('list')}>
              <LuList className="text-lg" />
            </button>
          </div>
          <label className="relative hidden sm:flex">
            <input
              className="form-input peer h-9 w-full rounded-full border border-slate-300 bg-transparent px-3 py-2 pl-9 text-xs+ placeholder:text-slate-400/70 hover:border-slate-400 focus:border-primary dark:border-navy-450 dark:hover:border-navy-400 dark:focus:border-accent"
              placeholder="Szukaj użytkowników"
              type="text"
              value={searchTerm}
              onChange={handleChange}
            />
            <span className="pointer-events-none absolute flex h-full w-10 items-center justify-center text-slate-400 peer-focus:text-primary dark:text-navy-300 dark:peer-focus:text-accent">
              <CiSearch />
            </span>
          </label>
          <Pagination
            currentPage={currentPage}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
            prvPage={prvPage}
            nextPage={nextPage}
            isDisabled={isFetching}
          />
        </div>
      </div>
      <button
        className="btn w-fit mb-8 bg-primary px-4 font-medium text-white hover:bg-primary-focus focus:bg-primary-focus active:bg-primary-focus/90 dark:bg-accent dark:hover:bg-accent-focus dark:focus:bg-accent-focus dark:active:bg-accent/90"
        onClick={goToUserForm}>
        <span>Stwórz nowego użytkownika</span>
      </button>

      <div className="mb-6 flex items-center gap-2">
        <button
          type="button"
          className={`btn h-9 rounded-full px-4 font-medium ${userFilterTab === 'all'
              ? 'bg-primary text-white dark:bg-accent'
              : 'bg-slate-150 text-slate-700 hover:bg-slate-200 dark:bg-navy-500 dark:text-navy-100 dark:hover:bg-navy-450'
            }`}
          onClick={() => handleUserFilterTabChange('all')}>
          Wszyscy
        </button>
        <button
          type="button"
          className={`btn h-9 rounded-full px-4 font-medium ${userFilterTab === 'inactive'
              ? 'bg-primary text-white dark:bg-accent'
              : 'bg-slate-150 text-slate-700 hover:bg-slate-200 dark:bg-navy-500 dark:text-navy-100 dark:hover:bg-navy-450'
            }`}
          onClick={() => handleUserFilterTabChange('inactive')}>
          Nieaktywne konta
        </button>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 pb-12 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
          {visibleUsers.map(user => <UserCard key={user.id} {...user} />)}
        </div>
      ) : (
        <div className="card overflow-hidden pb-12">
          <div className="overflow-x-auto">
            <div className="min-w-[860px]">
              <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(220px,1.2fr)] gap-4 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-navy-700 dark:text-navy-200 sm:px-5">
                <div>Użytkownik</div>
                <div>Rola</div>
                <div>Status</div>
                <div>Akcje</div>
              </div>
              <div>
                {visibleUsers.map(user => <UserListItem key={user.id} {...user} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
